import { useEffect } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { LockIcon, BuildingsIcon } from '@phosphor-icons/react';
import { Modal, ModalVariant } from '@/components/Modal';
import { RichSelect, type RichSelectOption } from '@/components/RichSelect';
import { TextField } from '@/components/TextField';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { EngineService } from '@/services/engine';
import type { FolderVisibility, SourceFolder } from '@/services/engine/types';
import { revalidateSourceFolders } from '@/hooks';
import { isConflictError } from '@/utils/http';
import { getApiErrorMessage } from '@/utils/error-messages';
import { toast } from '@/components';

const PERMISSION_PRIVATE: FolderVisibility = 'private';
const PERMISSION_ORGANIZATION: FolderVisibility = 'organization';

interface CreateFolderModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess?: (folder: SourceFolder, isEdit: boolean) => void;
    initialFolder?: SourceFolder | null;
}

interface FolderFormValues {
    name: string;
    visibility: FolderVisibility;
}

const buildDefaultValues = (
    initialFolder?: SourceFolder | null,
): FolderFormValues => ({
    name: initialFolder?.name ?? '',
    visibility: initialFolder?.isShared
        ? PERMISSION_ORGANIZATION
        : PERMISSION_PRIVATE,
});

export function CreateFolderModal({
    isOpen,
    onClose,
    onSuccess,
    initialFolder,
}: CreateFolderModalProps) {
    const { t } = useTranslation(I18nNamespace.SOURCES);

    const methods = useForm<FolderFormValues>({
        defaultValues: buildDefaultValues(initialFolder),
    });

    const {
        register,
        control,
        handleSubmit,
        reset,
        setError,
        clearErrors,
        setFocus,
        watch,
        formState: { errors, isSubmitting },
    } = methods;

    const isEditMode = !!initialFolder?.id;
    const nameValue = watch('name');

    const permissionOptions: RichSelectOption[] = [
        {
            value: PERMISSION_PRIVATE,
            title: t('folders.permissionPrivate'),
            description: t('folders.permissionPrivateDescription'),
            icon: LockIcon,
            iconWeight: 'fill',
        },
        {
            value: PERMISSION_ORGANIZATION,
            title: t('folders.permissionShared'),
            description: t('folders.permissionSharedDescription'),
            icon: BuildingsIcon,
            iconWeight: 'fill',
        },
    ];

    useEffect(() => {
        if (!isOpen) {
            return;
        }
        reset(buildDefaultValues(initialFolder));
        const frameId = requestAnimationFrame(() => setFocus('name'));
        return () => cancelAnimationFrame(frameId);
    }, [isOpen, initialFolder, reset, setFocus]);

    const handleSubmitSuccess = async (
        folder: SourceFolder,
        isEdit: boolean,
    ) => {
        reset(buildDefaultValues());
        onClose();
        await revalidateSourceFolders();
        onSuccess?.(folder, isEdit);
    };

    const onValidSubmit = async (formValues: FolderFormValues) => {
        const name = formValues.name.trim();
        const visibility = formValues.visibility;
        try {
            if (isEditMode && initialFolder) {
                const initialVisibility: FolderVisibility =
                    initialFolder.isShared
                        ? PERMISSION_ORGANIZATION
                        : PERMISSION_PRIVATE;
                const updated = await EngineService.updateSourceFolder(
                    initialFolder.id,
                    {
                        name,
                        ...(visibility !== initialVisibility && { visibility }),
                    },
                );
                await handleSubmitSuccess(updated, true);
            } else {
                const created = await EngineService.createSourceFolder({
                    name,
                    visibility,
                });
                await handleSubmitSuccess(created, false);
            }
        } catch (error) {
            if (isConflictError(error)) {
                setError('name', {
                    type: 'server',
                    message: t('folders.createFolderNameExists'),
                });
                setFocus('name');
                return;
            }
            toast.error(
                getApiErrorMessage(
                    error,
                    t(
                        isEditMode
                            ? 'folders.renameFolderError'
                            : 'folders.createFolderError',
                    ),
                ),
            );
        }
    };

    const handleClose = () => {
        if (!isSubmitting) {
            reset(buildDefaultValues());
            onClose();
        }
    };

    return (
        <FormProvider {...methods}>
            <Modal
                isOpen={isOpen}
                onClose={handleClose}
                title={
                    isEditMode
                        ? t('folders.editFolder')
                        : t('folders.createFolder')
                }
                onCancel={handleClose}
                onSubmit={() => handleSubmit(onValidSubmit)()}
                submitText={
                    isEditMode
                        ? t('folders.saveFolder')
                        : t('folders.createFolder')
                }
                submitDisabled={!nameValue?.trim()}
                submitInProgress={isSubmitting}
                size="medium"
                variant={ModalVariant.PLAIN}
                submitButtonTestId="folder-submit-btn"
            >
                <div className="flex flex-col gap-6 px-6 py-6">
                    <div className="flex flex-col gap-2">
                        <label
                            htmlFor="create-folder-name"
                            className="text-sm-medium text-white-100"
                        >
                            {t('folders.name')}{' '}
                            <span className="text-grey-300">*</span>
                        </label>
                        <TextField
                            {...register('name', {
                                required: true,
                                validate: (value) => !!value.trim(),
                                onChange: () => clearErrors('name'),
                            })}
                            id="create-folder-name"
                            data-testid="folder-name-input"
                            error={errors.name?.message}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleSubmit(onValidSubmit)();
                                }
                            }}
                            placeholder=""
                            required
                        />
                    </div>
                    <Controller
                        name="visibility"
                        control={control}
                        render={({ field }) => (
                            <RichSelect
                                label={t('folders.permissions')}
                                value={field.value}
                                options={permissionOptions}
                                onChange={field.onChange}
                            />
                        )}
                    />
                </div>
            </Modal>
        </FormProvider>
    );
}
